import React, { useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import { updateAppointment } from "../redux/slices/appointmentSlice";
import { AppDispatch } from "../redux/store";
import { AppointmentState } from "../types";
import { sendToWindowDataLayer } from "../utils/dom";
import {
  buttonPrimary,
  buttonSecondary,
  cardHeaderClass,
  cardBodyClass,
} from "css";
import { SlotInfo } from "types/available-slots.interface";

// Add hardcoded debug flag
const DEBUG_MODE = true;

interface TimePickerProps {
  appointment: AppointmentState;
  selectedTime: string;
  setSelectedTime: (time: string) => void;
  bookingTimes: SlotInfo[];
  loading: boolean;
  noSlotsMessage: string;
  setClear: (clear: boolean) => void;
  setBounceComplimentaryRide: (bounce: boolean) => void;
}

export const TimePicker: React.FC<TimePickerProps> = ({
  appointment,
  selectedTime,
  setSelectedTime,
  bookingTimes,
  loading,
  noSlotsMessage,
  setClear,
  setBounceComplimentaryRide,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const renderTimeButton = (slot: SlotInfo) => {
    const isSelected = slot.time === selectedTime;
    const isDisabled = !slot.isAvailable || slot.hasBlocker;

    return (
      <div
        key={slot.time}
        className="relative m-1 group"
        title={isDisabled ? slot.message : ""}
        onMouseEnter={() => {
          if (DEBUG_MODE) {
            console.log("SlotInfo for time:", slot.time, slot);
          }
        }}
      >
        <button
          type="button"
          disabled={isDisabled}
          className={`${isSelected ? buttonSecondary : buttonPrimary} ${
            isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
          }`}
          onClick={() => {
            if (isDisabled) return;

            dispatch(
              updateAppointment({
                updates: { time: slot.time },
              })
            );
            setSelectedTime(slot.time);
            sendToWindowDataLayer("dc-service-time", slot.time);

            if (!appointment?.shopData?.requireComments) {
              setClear(true);
            }

            if (appointment?.shopData?.complimentaryRide) {
              setBounceComplimentaryRide(false);
              setClear(false);
            }
          }}
          style={{
            backgroundColor: isSelected
              ? appointment?.shopData?.color
              : "white",
            border: `1px solid ${appointment?.shopData?.color}`,
            boxShadow:
              appointment?.shopData?.shadow === "shadow-md"
                ? `0 4px 6px ${appointment?.shopData?.color}33`
                : "none",
            color: isSelected ? "white" : "black",
          }}
        >
          {slot.time}
        </button>
        {isDisabled && (
          <div className="hidden group-hover:block absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded shadow-lg">
            {slot.message}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div
        id="time-picker"
        className={`${cardHeaderClass} flex justify-between`}
        style={{
          backgroundColor: appointment?.shopData?.color,
        }}
      >
        Select a Time Slot (Time slots reflect shop hours)
        <span className="relative flex h-3 w-3 pt-1.5">
          {selectedTime ? (
            <>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-green-600"></span>
            </>
          ) : (
            <>
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-red-600"></span>
            </>
          )}
        </span>
      </div>
      <div
        className={cardBodyClass}
        style={{
          borderColor: appointment?.shopData?.color,
        }}
      >
        {loading ? (
          <div className="col-span-3 md:col-span-6">
            <center>
              <img
                src="https://bdm-files.s3.amazonaws.com/images/spinners/Gear@1x-0.3s-200px-200px.gif"
                alt="loading"
              />
              <br />
              <br />
              Finding the best times for you!
            </center>
          </div>
        ) : !bookingTimes || bookingTimes.length === 0 ? (
          <div className="col-span-3 md:col-span-6">
            <div className="mx-6">
              <pre
                style={{
                  fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
                  whiteSpace: "pre-wrap",
                  overflowWrap: "break-word",
                  overflow: "auto",
                }}
              >
                {noSlotsMessage}
              </pre>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-3 md:grid-cols-6">
            {bookingTimes.map((slot) => renderTimeButton(slot))}
          </div>
        )}
      </div>
    </>
  );
};
